import { datePickerFormItemProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Alert, Form, Input, Modal, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateDashboardGoal, useEnumeration, useGetDashboardGoals } from "../../../../../../common/api/queries";
import { ModalSizes } from "../../../../../../common/constants";
import { UUID } from "../../../../../../typings/global";
import { EnumerationType } from "../../../../../enumerations/enums";
import { DashboardGoalType } from "../../../../enums";
import { CreateDashboardGoal } from "../../../../types";
import { getGoalActualValue, getGoalTargetValueName } from "../utils";
import { TargetValueFormInput } from "./parts/TargetValueFormInput";

type Props = {
  isOpen: boolean;
  onCancel: VoidFunction;
  goalType?: DashboardGoalType;
  goalTypeOptions?: DashboardGoalType[];
  year: number;
  agentId: UUID;
};

export const AddGoalModal = ({ isOpen, onCancel, goalType, agentId, year, goalTypeOptions }: Props) => {
  const { t } = useTranslation();

  const commissionsSettingsLevelsEnums = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_LEVELS);
  const commissionsSettingsRanksEnums = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_RANKS);

  const { data } = useGetDashboardGoals({ id: agentId ?? "", object: { year } });

  const [form] = Form.useForm<CreateDashboardGoal>();
  const formGoalType = Form.useWatch("type", form);

  const targetValueName = getGoalTargetValueName(formGoalType);
  const currentGoalData = data?.find((goal) => goal.type === formGoalType);

  const createGoal = useCreateDashboardGoal();

  const goalTypeOptionItems = goalTypeOptions ?? Object.values(DashboardGoalType);

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({ year: year, type: goalType });
    }
  }, [isOpen, goalType, year]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      createGoal.mutate(
        { id: agentId, object: values },
        {
          onSuccess: onCancel,
          onError: (error) => {
            setErrorsToForm(form, "dashboard.goals.attrs", error.violations);
          },
        },
      );
    });
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={isOpen}
      onCancel={onCancel}
      title={goalType ? t("dashboard.goals.titles.setGoal") : t("dashboard.goals.titles.addGoal")}
      onOk={handleSubmit}
      confirmLoading={createGoal.isPending}
      destroyOnHidden
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" name="addGoalForm">
        <Form.Item
          name="year"
          label={t("dashboard.goals.attrs.year")}
          initialValue={year}
          rules={[validations.notNull]}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="type"
          label={t("dashboard.goals.attrs.type")}
          rules={[validations.notNull]}
          initialValue={goalType}
        >
          <Select
            disabled={!!goalType}
            options={goalTypeOptionItems.map((type) => ({
              label: t(`dashboard.enums.dashboardGoalType.${type}`),
              value: type,
            }))}
          />
        </Form.Item>

        <Form.Item
          name={targetValueName}
          label={t(`dashboard.goals.attrs.${targetValueName}`)}
          rules={[validations.notNull]}
          {...(targetValueName === "targetDate" ? datePickerFormItemProps : {})}
        >
          {formGoalType ? (
            <TargetValueFormInput
              goalType={formGoalType}
              year={year}
              level={{
                currentValue: currentGoalData?.actualCommissionsLevel,
                options: commissionsSettingsLevelsEnums,
              }}
              rank={{
                currentValue: currentGoalData?.actualCommissionsRank,
                options: commissionsSettingsRanksEnums,
              }}
            />
          ) : (
            <Alert title={t("dashboard.goals.helpers.setGoalHint")} type="info" showIcon />
          )}
        </Form.Item>

        <p className="sub-header-info">{currentGoalData ? getGoalActualValue(currentGoalData) : undefined}</p>
      </Form>
    </Modal>
  );
};
